import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { FunctionsService } from 'src/app/shared/functions.service';
import { AuthService } from 'src/app/shared/auth.service';
import { Title } from '@angular/platform-browser';
import { environment } from 'src/environments/environment';

interface RecordingSummary {
  id: string;
  name: string;
  date: string;
  ready: boolean;
  deleted: boolean;
}

interface TeamRecordingsResponse {
  teamName: string;
  recordings: RecordingSummary[];
  hasRestrictedPrivateRecordings: boolean;
  hasRestrictedUnlistedRecordings: boolean;
}

@Component({
  selector: 'app-team-recordings',
  standalone: true,
  imports: [CommonModule, TranslateModule, RouterModule],
  templateUrl: './team-recordings.component.html',
  styleUrls: ['./team-recordings.component.scss'],
})
export class TeamRecordingsComponent implements OnInit {
  private fns = inject(FunctionsService);
  private route = inject(ActivatedRoute);
  public router = inject(Router);
  private auth = inject(AuthService);
  private titleService = inject(Title);

  recordings = signal<RecordingSummary[]>([]);
  isLoading = signal(true);
  isLoggedIn = signal(false);
  error = signal<string | null>(null);

  hasPrivateRestricted = signal(false);
  hasUnlistedRestricted = signal(false);

  currentFilter = signal<number>(30);
  readonly filterOptions = [7, 30, 90, 180, 3650];

  teamId: string | null = null;
  sharingKey: string | null = null;

  ngOnInit() {
    this.teamId = this.route.snapshot.paramMap.get('id');
    this.sharingKey = this.route.snapshot.queryParamMap.get('s');
    this.isLoggedIn.set(this.auth.isLoggedIn());

    if (!this.teamId) {
      this.error.set('errors.team-not-found');
      this.isLoading.set(false);
      return;
    }

    this.loadRecordings();
  }

  // Helper for template to bind query params cleanly
  get recordingParams() {
    return this.sharingKey ? { s: this.sharingKey } : {};
  }

  setFilter(days: number) {
    if (this.currentFilter() === days) return;
    this.currentFilter.set(days);
    this.loadRecordings();
  }

  async loadRecordings() {
    this.isLoading.set(true);
    this.error.set(null);

    try {
      const response = await this.fns.callFunction('getTeamRecordings', {
        teamId: this.teamId,
        daysAgo: this.currentFilter(),
        sharingKey: this.sharingKey 
      }) as TeamRecordingsResponse;

      
      this.titleService.setTitle(`${response.teamName} | ${environment.appName}`);

      this.recordings.set(response.recordings || []);
      this.hasPrivateRestricted.set(response.hasRestrictedPrivateRecordings);
      this.hasUnlistedRestricted.set(response.hasRestrictedUnlistedRecordings);

    } catch (err: any) {
      console.error('Error loading team recordings', err);
      const code = err.message || 'error.unknown-error';
      this.error.set(code);
    } finally {
      this.isLoading.set(false);
    }
  }
}
